import type {
  CatalogueFilters,
  CatalogueProperty,
  PriceSummary,
  PropertyStatus,
  PropertyType,
} from "@/types";
import { capitalize } from "./format";

/** In-memory tally of catalogue rows by status. Not part of the API surface. */
export type CatalogueStatusCounts = Record<PropertyStatus, number>;

export function formatPropertyTypeLabel(type: PropertyType | "all"): string {
  if (type === "all") return "All";
  return capitalize(type);
}

function formatAmount(amount: string | number): string {
  const n = typeof amount === "number" ? amount : Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

function rateSuffix(price: PriceSummary): string {
  switch (price.rateType) {
    case "per_week":
      return "/week";
    case "per_year":
      return "/year";
    case "total_nights":
      return price.nights && price.nights > 0
        ? `/ ${price.nights} night${price.nights === 1 ? "" : "s"}`
        : "total";
    case "per_night":
    default:
      return "/night";
  }
}

/** Format a list-row price summary. Returns "" when null/absent. */
export function formatPriceSummary(
  price: PriceSummary | null | undefined,
): string {
  if (!price?.currency || price.amount == null || price.amount === "") {
    return "";
  }
  const suffix = rateSuffix(price);
  const body =
    price.mode === "range" && price.amountMax
      ? `${formatAmount(price.amount)} - ${formatAmount(price.amountMax)}`
      : formatAmount(price.amount);
  return `${price.currency} ${body} ${suffix}`.replace(/\s+/g, " ").trim();
}

export function formatNightlyPrice(
  amount: number,
  currency: string,
): string {
  if (!currency && !amount) return "";
  const formatted = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(amount);
  return `${currency} ${formatted}/night`.trim();
}

export function formatPriceRange(
  min: number,
  max: number,
  currency: string,
): string {
  if (!currency && !min && !max) return "";
  const fmt = (n: number) =>
    new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
  return `${currency} ${fmt(min)} - ${fmt(max)} /night`.trim();
}

export function countCatalogueStatuses(
  properties: CatalogueProperty[],
): CatalogueStatusCounts {
  return {
    published: properties.filter((p) => p.status === "published").length,
    approved: properties.filter((p) => p.status === "approved").length,
    pending: properties.filter((p) => p.status === "pending").length,
    rejected: properties.filter((p) => p.status === "rejected").length,
    draft: properties.filter((p) => p.status === "draft").length,
  };
}

/**
 * Client-side residual filter for type. Everything else (q, beds, guests,
 * ratings, supplier name, cost bounds, addedBy) is sent to the server via
 * GET /properties. Type is still applied here so a stale or unscoped list
 * response cannot leak another category's rows.
 */
export function filterCatalogueProperties(
  properties: CatalogueProperty[],
  filters: CatalogueFilters,
): CatalogueProperty[] {
  return properties.filter((property) => {
    if (
      filters.propertyType !== "all" &&
      property.type &&
      property.type !== filters.propertyType
    ) {
      return false;
    }
    return true;
  });
}

export function isPropertyStatus(value: string): value is PropertyStatus {
  return (
    value === "published" ||
    value === "approved" ||
    value === "pending" ||
    value === "rejected" ||
    value === "draft"
  );
}

export function isPropertyType(value: string): value is PropertyType {
  return (
    value === "villa" ||
    value === "chalet" ||
    value === "apartment" ||
    value === "island" ||
    value === "castle"
  );
}
